import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import type {SocialMediaPublishingParams} from '@Components/social-media/publish-params.types';
import type {PlatformPostPreviewProps} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {getGoogleBusinessProfileCtaText} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-multiposting.types';
import {PreviewMediaFit, PreviewMediaLayout} from '@Components/social-media/components/social-post-content-multiposting/social-post-content-media-wrapper/social-post-content-media-wrapper.types';
import {PreviewAccountHeader} from '@Components/social-media/components/social-post-content-multiposting/components/preview-account-header/preview-account-header';
import {PreviewAccountMetaSkeleton} from '@Components/social-media/components/social-post-content-multiposting/components/preview-account-header/components/preview-account-meta-skeleton/preview-account-meta-skeleton';
import {PREVIEW_ACCOUNT_META_BAR_ONLY} from '@Components/social-media/components/social-post-content-multiposting/components/preview-account-header/components/preview-account-meta-skeleton/preview-account-meta-skeleton.types';
import {PreviewClampedCaption} from '@Components/social-media/components/social-post-content-multiposting/components/preview-clamped-caption/preview-clamped-caption';
import styles from './google-business-post-preview.module.scss';
import {getCaptionFromPublishingParams} from '@Libraries/social-media-params.library';

export function GoogleBusinessPostPreview({account, platformMediaType, publishingParams, renderMedia}: PlatformPostPreviewProps): ReactElement {
  const getParamsForMediaType = (): SocialMediaPublishingParams | null | undefined => {
    return publishingParams[account.id]?.[platformMediaType];
  };

  const getCaptionText = (): string => {
    return getCaptionFromPublishingParams(publishingParams, account.id, platformMediaType) || window.i18next.t('pmwjs_post_update_for_x', {accountName: account.name});
  };

  const getCaption = (): ReactElement => {
    const captionText = getCaptionText();

    return (
      <div className={styles.googleBusinessCaptionHolder}>
        <PreviewClampedCaption captionText={captionText} textClassName={styles.googleBusinessCaptionClamp}>
          <Text className={styles.googleBusinessCaptionText} size={TextSize.XS} val={captionText} />
        </PreviewClampedCaption>
      </div>
    );
  };

  const getCallToAction = (): ReactElement | null => {
    const paramsForMediaType = getParamsForMediaType();

    if (!paramsForMediaType || !('ctaType' in paramsForMediaType) || paramsForMediaType.ctaType === null) {
      return null;
    }

    return (
      <div className={styles.googleBusinessCallToActionRow}>
        <Text className={styles.googleBusinessCallToAction} size={TextSize.XS} val={getGoogleBusinessProfileCtaText(paramsForMediaType.ctaType)} />
      </div>
    );
  };

  return (
    <div className={styles.googleBusinessPostPreview}>
      <PreviewAccountHeader
        account={account}
        className={styles.googleBusinessHeaderRow}
        metaRow={<PreviewAccountMetaSkeleton parts={PREVIEW_ACCOUNT_META_BAR_ONLY} />}
        trailingAction={null}
      />
      {renderMedia({layout: PreviewMediaLayout.SINGLE, fit: PreviewMediaFit.COVER, frameClassName: styles.googleBusinessMediaFrame})}
      {getCaption()}
      {getCallToAction()}
    </div>
  );
}
